import { FC, useEffect } from 'react';

import { Container, VisuallyHidden } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { withParams } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import { InitiateCheckoutProps } from '../../../hooks/useInitiateSelfServiceCheckout';
import { usePlansGA } from '../../../hooks/usePlansGA';
import { Commitment, OfferGroup, ServiceOffersResponse } from '../../../model';
import { FacetFilteringControls } from './Facets/model';
import { NboConsent } from './NboConsent/NboConsent';
import { NoNboOffers } from './NoNboOffers/NoNboOffers';
import { PlansBlockHeader } from './PlansBlockHeader/PlansBlockHeader';
import { PlansList } from './PlansList';
import { PlansListSkeleton } from './PlansListSkeleton';

type Props = {
    data: ServiceOffersResponse;
    hasFilters?: boolean;
    agreementId?: string;
    msisdn?: string;
    hasSorting: boolean;
    initialSortLabel?: string;
    commitmentKey?: string;
    defaultCommitmentKey?: string;
    teliaOneKey?: string;
    isLoading: boolean;
    offerGroup: OfferGroup;
    ctaLoadingUoId?: string;
    possibleCommitmentTypes: Commitment[];
    isConsentVisible?: boolean;
    noNboOffersAvailable?: boolean;
    onCheckout?: (props: InitiateCheckoutProps) => void;
    isFetching: boolean;
};

export const PlansBlock: FC<Props & FacetFilteringControls> = ({
    data,
    hasSorting,
    hasFilters,
    initialSortLabel,
    commitmentKey,
    defaultCommitmentKey,
    teliaOneKey,
    agreementId,
    msisdn,
    isLoading,
    appliedFilters,
    facets,
    filters,
    offerGroup,
    onFilterChange,
    onRemoveFilter,
    onRemoveAllFilters,
    ctaLoadingUoId,
    possibleCommitmentTypes,
    isConsentVisible,
    noNboOffersAvailable,
    onCheckout,
    isFetching,
}) => {
    const hasTelia1Toggle = !!data?.facets?.find((facet) => facet.key === 'isTeliaOne');

    const { t } = useTranslations();
    const listId = useComponentId('plans-list');
    const { isLoading: isGaEventsLoading, onViewItemList, onSelectItem, onSelectModifyService } = usePlansGA();

    useEffect(() => {
        if (!isGaEventsLoading) {
            onViewItemList(data?.items || []);
        }
    }, [data?.items, onViewItemList, isGaEventsLoading]);

    const a11yLiveReader = (
        <VisuallyHidden aria-live="polite" aria-atomic={true} id={listId}>
            {isFetching
                ? t['plans.plans_list.aria_live.loading']
                : withParams(t['plans.plans_list.aria_live.list_updated'], {
                      items: data?.items?.map((item) => item.name).join(', '),
                  })}
        </VisuallyHidden>
    );

    if (isLoading) {
        return (
            <Container>
                {a11yLiveReader}
                <PlansListSkeleton />
            </Container>
        );
    }

    const header = (
        <PlansBlockHeader
            possibleCommitmentTypes={possibleCommitmentTypes}
            hasSorting={hasSorting}
            hasFilters={hasFilters}
            initialSortLabel={initialSortLabel}
            commitmentKey={commitmentKey}
            defaultCommitmentKey={defaultCommitmentKey}
            teliaOneKey={teliaOneKey}
            hasTelia1Toggle={hasTelia1Toggle}
            facets={facets}
            filters={filters}
            appliedFilters={appliedFilters}
            onFilterChange={onFilterChange}
            onRemoveFilter={onRemoveFilter}
            onRemoveAllFilters={onRemoveAllFilters}
            listId={listId}
            offerGroup={offerGroup}
        />
    );

    if (isFetching) {
        return (
            <Container>
                {header}
                {a11yLiveReader}
                <PlansListSkeleton />
            </Container>
        );
    }
    if (isConsentVisible) {
        return (
            <Container>
                {header}
                {a11yLiveReader}
                <NboConsent
                    agreementId={agreementId}
                    msisdn={msisdn}
                    offerGroup={offerGroup}
                    possibleCommitmentTypes={possibleCommitmentTypes}
                />
            </Container>
        );
    }
    if (noNboOffersAvailable) {
        return (
            <Container>
                {header}
                {a11yLiveReader}
                <NoNboOffers />
            </Container>
        );
    }

    return (
        <Container>
            {header}
            {a11yLiveReader}
            <PlansList
                items={data?.items || []}
                msisdn={msisdn}
                onSelectItem={onSelectItem}
                onSelectModifyService={onSelectModifyService}
                ctaLoadingUoId={ctaLoadingUoId}
                onCheckout={onCheckout}
            />
        </Container>
    );
};
